<template>
    <AuditsTable></AuditsTable>
    <ViewAuditModal
        v-if="viewModalOpen"
        v-model:open="viewModalOpen"
        :audit="selectedAudit"
    />
</template>

<script setup lang="ts">
import { useTable } from '@src/composables/common'
import { TableSorting } from '@src/types'
import { Audit, getAudits } from '@src/utils/api/audits'
import moment from 'moment'
import { onMounted, ref, toRefs } from 'vue'
import ViewAuditModal from './ViewAuditModal.vue'

const props = withDefaults(
    defineProps<{
        initialFilters?: {
            auditable_type?: string | null
            auditable_id?: string | null
            event?: string | null
            date_from?: string | null
            date_to?: string | null
        } | null
    }>(),
    {
        initialFilters: null,
    }
)

const { initialFilters } = toRefs(props)

const audits = ref<Audit[]>([])
const auditsCount = ref<number>(0)
const viewModalOpen = ref(false)
const selectedAudit = ref<Audit>({} as Audit)

const {
    TableWrapper: AuditsTable,
    limit,
    sorting,
    page,
    filtersFields,
    filters,
    setFilters,
} = useTable({
    loadData: loadAudits,
    filters: {
        auditable_type: null,
        auditable_id: null,
        event: null,
        date_from: null,
        date_to: null,
    },
    disableFilters: initialFilters.value
        ? Object.keys(initialFilters.value)
        : [],
    tableProps: {
        id: 'audits-table',
        label: 'audits',
        help: 'help_audits_table',
        pagination: true,
        data: audits,
        total: auditsCount,
        sorting: ref<TableSorting[]>([
            {
                field: 'created_at',
                direction: 'desc',
            },
        ]),
        columns: [
            {
                label: 'auditable_type',
                key: 'auditable_type',
                cellRenderer: (props: { rowData: Audit }) => {
                    return props.rowData.auditable_type.split('\\')[2]
                },
            },
            {
                label: 'auditable_id',
                key: 'auditable_id',
            },
            {
                label: 'event',
                key: 'event',
            },
            {
                label: 'created_at',
                key: 'created_at',
                cellRenderer: (props: { rowData: Audit }) => {
                    return moment(props.rowData.created_at).format(
                        'DD.MM.yyyy HH:mm'
                    )
                },
            },
        ],
        actions: [
            {
                action: (audit: Audit) => {
                    selectedAudit.value = audit
                    viewModalOpen.value = true
                },
                icon: 'fa-eye',
                title: 'view',
            },
        ],
    },
})

async function loadAudits() {
    const response = await getAudits(
        filters.value,
        page.value,
        limit.value,
        sorting.value
    )
    if (response.data.success && response.status === 206) {
        audits.value = response.data.audits
        auditsCount.value = response.data.audits_count
    }
}

onMounted(async () => {
    if (initialFilters.value) {
        filtersFields.value = {
            ...filtersFields.value,
            ...initialFilters.value,
        }
        setFilters()
        await loadAudits()
    }
})
</script>
